import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RATE_IT_MILESTONE_COLORS, TIRI_HEADER } from '../constants';

/**
 * Positioned inside the SLIDE, not over the screen.
 *
 * It used to be a child of the feed's root, which is the full viewport — so above the desktop
 * breakpoint, where the moment becomes a centred 9:16 stage, the banner ran the whole width of
 * the window and crossed well outside the moment it describes. As a child of the stage it
 * inherits the stage's width at every breakpoint and needs no media query of its own.
 *
 * `top` clears the header's close/TIRI row. (It used to clear a creator row beneath it as well;
 * that row now sits at the bottom of the card, above the tiles.)
 */
const Banner = styled.div`
  position: absolute;
  top: ${TIRI_HEADER.TOP_OFFSET + TIRI_HEADER.BUTTON_SIZE + TIRI_HEADER.ROW_GAP}px;
  left: 12px;
  right: 12px;
  /* Above the header (10), which it sits directly beneath and must never be overlapped by. */
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${RATE_IT_MILESTONE_COLORS.CTA};
`;

/**
 * The sender's avatar, falling back to their initial.
 *
 * `avatar` is genuinely optional on `User` — plenty of accounts have none — and the empty grey
 * disc this rendered before read as a broken image rather than as an account without a picture.
 * Initials are what the app-wide `components/Avatar` falls back to in the same situation.
 */
const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  background: ${(props) =>
    props.$src ? `url(${props.$src}) center / cover no-repeat` : 'rgba(255,255,255,0.22)'};
`;

const Copy = styled.p`
  margin: 0;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${RATE_IT_MILESTONE_COLORS.WHITE};
`;

/**
 * Stands in for the name while the sender resolves.
 *
 * Doubles as the <Trans> wrapper for the name segment, which is why it hides its own text rather
 * than being rendered empty: the sentence is ONE translated string, so the placeholder has to
 * occupy the name's position within it — and that position carries the "@" and whatever else a
 * translator put around it.
 */
const NameSkeleton = styled.span`
  display: inline-block;
  width: 84px;
  height: 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
  vertical-align: middle;
  color: transparent;
  overflow: hidden;
`;

/**
 * The brief's referral banner: "@{Name} invited you — rate 5 moments and you both earn N points."
 *
 * Rendered over the FIRST card only, and dismissed once the fan moves on: it introduces the
 * shared moment, and by the second moment it would be describing something the fan is no longer
 * looking at.
 *
 * Three states, and the failure one matters. While the sender resolves, only the NAME is a
 * skeleton — the moment behind must never wait on a banner. If the sender cannot be resolved at
 * all (deleted account, malformed link) the attribution clause is dropped but the offer is kept,
 * because the referral is still valid server-side and the fan can still earn it. Rendering
 * nothing there would silently hide a live reward.
 */
const RateItReferralBanner = ({ sender, points, isLoading, threshold = 5 }) => {
  const { t } = useTranslation();
  const name = sender?.username || sender?.name;

  /* A KNOWN sender wins over the loading flag, never the other way round. The sender can arrive
     from either of two calls — the public resolve, or the activate response — so the banner
     renders the name the moment it has one rather than waiting on whichever request happened to
     be tracked. */
  const attributed = !!sender || isLoading;

  return (
    <Banner>
      <Avatar $src={sender?.avatarUrl}>{sender?.avatarUrl ? null : name?.[0] || 'F'}</Avatar>
      <Copy>
        {attributed ? (
          /* One sentence, ONE key: the name sits inside the translated string so a language can
             move it, which a hand-assembled "<strong>{name}</strong> + the rest" cannot do. The
             wrapper swaps to the skeleton while the name is still in flight. */
          <Trans components={{ strong: sender ? <strong /> : <NameSkeleton /> }}>
            {t('tiri.rateIt.referral.banner', {
              defaultValue:
                '<strong>@{{name}}</strong> invited you — rate {{count}} moments and you both earn {{points}} points.',
              name: name || '',
              count: threshold,
              points
            })}
          </Trans>
        ) : (
          t('tiri.rateIt.referral.offer', {
            defaultValue: 'Rate {{count}} moments and you both earn {{points}} points.',
            count: threshold,
            points
          })
        )}
      </Copy>
    </Banner>
  );
};

export default RateItReferralBanner;
